import React, { Dispatch, FC, SetStateAction, useEffect, useMemo, useRef, useState } from 'react'
import { ExpandLess, ExpandMore } from '@material-ui/icons'
import { Collapse } from '@material-ui/core'
import { useSelector } from 'react-redux'

import { alertSuccess, alertError } from 'api'
import Reducers from 'reducers'
import { NewDeleteIcon } from 'uiKit/icons/NewDeleteIcon'
import Tooltip from 'uiKit/Tooltip'
import LoaderSmall from 'uiKit/loaders/loaderSmall'
import LoaderOverlay from 'uiKit/loaders/loaderOverlay'
import { deleteSourceSubPage, getKnowledgeSourcePages, SourceTypeMap } from 'tabs/nlp/api/websiteKnowledgeBase'
import { TrainingStatus } from 'tabs/settings/constants/trainingStatus'

import * as S from './SourcesTable.style'
import TrainingStatusTitle from '../TrainingStatus'
import FailedStatus from '../FailedStatus'
import UploadedStatus from '../UploadedStatus'
import { DetectOverflow } from '../DetectOverflow/DetectOverflow'
import { SourcesSubPages } from './SourcesSubPages'
import { EditTextInsert } from '../../EditTextInsert/EditTextInsert'
import { EditKnowledgeBaseReply, TextInsertSource } from '../../KnowledgeBaseReplies/EditKnowledgeBaseReply'
import { DeleteSourceModal } from './DeleteSourceModal'
import { DeleteSubPageModal, WebsiteSource } from './DeleteSubPageModal'
import { HierarchyLevel } from './SourcesTable.style'

interface SourcesTableProps {
  sources: any
  setSources?: Dispatch<SetStateAction<any>>
  fetchKnowledgeSources?: () => Promise<void>
  onDelete: (source: any) => Promise<void>
  onTrain: () => void
  trainingStatus: string
  sourceId?: string | undefined | null
}

export const SourcesTable: FC<SourcesTableProps> = ({
  sources,
  setSources,
  fetchKnowledgeSources,
  onDelete,
  onTrain,
  trainingStatus,
  sourceId,
}) => {
  const botId: number = useSelector((state: ReturnType<typeof Reducers>) => state.activeBot.id)
  const [loading, setLoading] = useState(false)
  const [loadingSubPageDelete, setLoadingSubPageDelete] = useState(false)
  const [subPagesLoading, setSubPagesLoading] = useState(false)
  const [expanded, setExpanded] = useState(null)
  const [deleteConfirmationModal, setDeleteConfirmationModal] = useState<{ source: any; isOpen: boolean }>({
    source: null,
    isOpen: false,
  })
  const [deleteSubPageConfirmationModal, setDeleteSubPageConfirmationModal] = useState<{
    source: WebsiteSource
    isOpen: boolean
  }>({ source: null, isOpen: false })
  const [knowledgePages, setKnowledgePages] = useState({})
  const [insertTextSource, setInsertTextSource] = useState<TextInsertSource>(null)
  const highlightRef = useRef(null)

  const sourcesPending = useMemo(
    () => sources.filter(source => ['PENDING', 'FAILED', 'NOT_TRAINED'].includes(source.status)),
    [sources],
  )
  const sourcesTrained = useMemo(() => sources.filter(source => source.status === 'READY'), [sources])
  const sourcesFailed = useMemo(() => sources.some(source => source.status === 'FAILED'), [sources])
  const sourcesNotTrained = useMemo(() => sources.some(source => source.status === 'NOT_TRAINED'), [sources])
  // Same as PENDING: if any source is NOT_TRAINED, lock all deletes + accordions
  const isTableContentDisabled =
    [TrainingStatus.PENDING, TrainingStatus.UPLOADING].includes(trainingStatus) || sourcesNotTrained

  const getSourceName = source => {
    const sourceNameToSourceTypeMap = {
      [SourceTypeMap.FILE]: source.originalFilename,
      [SourceTypeMap.TEXT]: source.text,
      [SourceTypeMap.WEBSITE]: source.url,
    }
    return sourceNameToSourceTypeMap[source.type]
  }

  const handleExpand = (sourceId: string) => {
    if (sourceId === expanded) {
      return setExpanded(null)
    }
    setExpanded(sourceId)

    if (knowledgePages[sourceId]) return

    setSubPagesLoading(true)
    fetchSourceSubPages(sourceId)
  }

  const fetchSourceSubPages = async (sourceId: string): Promise<void> => {
    getKnowledgeSourcePages(sourceId, botId)
      .then(pages => setKnowledgePages({ ...knowledgePages, [sourceId]: Array.isArray(pages) ? pages : [] }))
      .finally(() => setSubPagesLoading(false))
  }

  const handleDelete = source => {
    if (source.isUploaded === false) {
      onDelete(source)
      resetDeleteConfirmationModal()
      return
    }
    setLoading(true)
    onDelete(source).finally(() => {
      setLoading(false)
      resetDeleteConfirmationModal()
    })
  }

  const handleDeleteSubPage = (source: WebsiteSource) => {
    const sourceId = source.sourceId
    const pageId = source._id
    setLoadingSubPageDelete(true)
    deleteSourceSubPage(botId, sourceId, pageId)
      .then(async () => {
        alertSuccess('Subpage was deleted successfully')
        fetchKnowledgeSources && (await fetchKnowledgeSources())
        await fetchSourceSubPages(sourceId)
        resetDeleteSubPageConfirmationModal()
      })
      .catch(error => alertError(error?.message || 'Something went wrong'))
      .finally(() => setLoadingSubPageDelete(false))
  }

  const resetDeleteConfirmationModal = (): void => {
    setDeleteConfirmationModal({ source: null, isOpen: false })
  }

  const resetDeleteSubPageConfirmationModal = (): void => {
    setDeleteSubPageConfirmationModal({ source: null, isOpen: false })
  }

  const handleOpenEditTextInsert = ({ sourceId, text, title }: TextInsertSource): void => {
    setInsertTextSource({ sourceId, title, text })
  }

  useEffect(() => {
    if (highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [highlightRef.current])

  const renderTrainingStatusTitle = () => {
    const isUploading = trainingStatus === TrainingStatus.UPLOADING
    const statusTitle = isUploading ? 'Uploading' : 'Bot is training'
    const statusTooltip = isUploading
      ? null
      : 'Training may take some time. Once training is completed, we will send you an email notification.'
    return <TrainingStatusTitle title={statusTitle} tooltip={statusTooltip} />
  }

  const renderHeaderContent = () => {
    if (isTableContentDisabled) {
      return renderTrainingStatusTitle()
    }
    if (sourcesFailed) {
      return <S.Button onClick={onTrain}>Train again</S.Button>
    }
    return null
  }

  const labels = [
    { value: 'FAQS_FLOWS', label: 'FAQs/flows' },
    { value: 'COMPANY_DOC', label: 'Careco docs' },
    { value: 'GENERIC_DOC', label: 'Generic' },
  ]

  const getLabelByValue = (value) => {
    const item = labels.find(label => label.value === value)
    return item ? item.label : null
  }

  return (
    <>
      <S.SourcesTable>
        {!!sourcesPending?.length && (
          <>
            <S.HeaderWrap>
              <S.Header>TO TRAIN</S.Header>
              {renderHeaderContent()}
            </S.HeaderWrap>
            {sourcesPending.map((source, index) => (
              <S.Row key={source._id} index={index}>
                <div style={{ flex: '1 1 100%', minWidth: 0, display: 'grid' }}>
                  <S.Source sourceType={source.type}>
                    {source.type === SourceTypeMap.TEXT ? (
                      <>
                        <h6>{source.title || 'Untitled'}</h6>
                        {getSourceName(source)}
                      </>
                    ) : (
                      <>
                        <DetectOverflow>{getSourceName(source)}</DetectOverflow>
                      </>
                    )}
                  </S.Source>
                </div>
                <HierarchyLevel>
                  <h6>
                    {getLabelByValue(source.hierarchyLevel)}
                  </h6>
                </HierarchyLevel>
                <S.DeleteWrap>
                  {source.type === SourceTypeMap.FILE && source.status !== 'FAILED' && (
                    <>
                      <UploadedStatus isUploaded={source.isUploaded} />
                    </>
                  )}
                  {source.status === 'FAILED' && <FailedStatus />}
                  <S.Delete
                    disabled={isTableContentDisabled}
                    onClick={() => setDeleteConfirmationModal({ source, isOpen: true })}>
                    <NewDeleteIcon />
                  </S.Delete>
                </S.DeleteWrap>
              </S.Row>
            ))}
          </>
        )}
        {!!sourcesTrained?.length && (
          <>
            <S.HeaderWrap>
              <S.Header>TRAINED</S.Header>
            </S.HeaderWrap>
            {sourcesTrained.map((source, index) => (
              <React.Fragment key={source._id}>
                <S.Row
                  index={index}
                  needHighlight={source._id === sourceId}
                  ref={source._id === sourceId ? highlightRef : null}>
                  <div style={{ flex: '1 1 100%', minWidth: 0, display: 'grid' }}>
                    <S.Source sourceType={source.type}>
                      {source.type === 'TEXT' ? (
                        <>
                          <h6>{source.title || 'Untitled'}</h6>
                          {getSourceName(source)}
                        </>
                      ) : (
                        <DetectOverflow>{getSourceName(source)}</DetectOverflow>
                      )}
                    </S.Source>
                  </div>
                  <HierarchyLevel>
                    <h6 style={{textAlign: 'left'}}>
                      {getLabelByValue(source.hierarchyLevel)}
                    </h6>
                  </HierarchyLevel>
                  <S.DeleteWrap>
                    {source.parsedLinksCount ? (
                      <>
                        <S.Count
                          disabled={isTableContentDisabled}
                          data-for={`source-pages-${source._id}`}
                          data-tip
                          onClick={() => handleExpand(source._id)}>
                          {source.parsedLinksCount}
                          {expanded === source._id ? <ExpandLess /> : <ExpandMore />}
                        </S.Count>
                        <Tooltip id={`source-pages-${source._id}`}>Amount of trained pages</Tooltip>
                      </>
                    ) : null}
                    {source.type === SourceTypeMap.TEXT && (
                      <EditTextInsert
                        onClick={() =>
                          handleOpenEditTextInsert({ sourceId: source._id, title: source.title, text: source.text })
                        }
                        wasEdited={false}
                      />
                    )}
                    <S.Delete
                      disabled={isTableContentDisabled}
                      onClick={() => setDeleteConfirmationModal({ source, isOpen: true })}>
                      <NewDeleteIcon />
                    </S.Delete>
                  </S.DeleteWrap>
                </S.Row>

                <Collapse in={expanded === source._id}>
                  <S.SourceDetails index={index} expanded={expanded === source._id}>
                    {subPagesLoading && expanded === source._id ? (
                      <LoaderSmall showLoader />
                    ) : (
                      <SourcesSubPages
                        parentRowColor={index % 2 === 0 ?
                          'var(--color-selago-grey)' : 'var(--color-table-row-primary)'}
                        sourceId={source._id}
                        title="List of trained pages"
                        subPages={knowledgePages[source._id] || []}
                        setDeleteSubPageConfirmationModal={setDeleteSubPageConfirmationModal}
                      />
                    )}
                  </S.SourceDetails>
                </Collapse>
              </React.Fragment>
            ))}
          </>
        )}
        {isTableContentDisabled && <S.DisableScreen />}
      </S.SourcesTable>
      {insertTextSource?.sourceId && (
        <EditKnowledgeBaseReply
          botId={botId}
          isOpen
          onClose={() => setInsertTextSource(null)}
          setSources={setSources}
          sourceId={insertTextSource.sourceId}
          inputTitle={insertTextSource.title || 'Untitled'}
          textAreaContent={insertTextSource.text || ''}
        />
      )}
      {deleteConfirmationModal.isOpen && (
        <DeleteSourceModal
          open
          onClose={resetDeleteConfirmationModal}
          onDelete={() => handleDelete(deleteConfirmationModal.source)}
        />
      )}
      {deleteSubPageConfirmationModal.isOpen && (
        <DeleteSubPageModal
          open
          url={deleteSubPageConfirmationModal.source.url}
          loading={loadingSubPageDelete}
          onClose={resetDeleteSubPageConfirmationModal}
          onDelete={() => handleDeleteSubPage(deleteSubPageConfirmationModal.source)}
        />
      )}
      {loading && <LoaderOverlay showLoader={loading} />}
    </>
  )
}
